export type SwitchRefreshFailureDecision =
  | 'clear'
  | 'preserve'
  | 'retry-latest'
  | 'rethrow';

export function decideSwitchRefreshFailure({
  attemptedRefreshToken,
  latestRefreshToken,
  isDefinitive,
  supportsLock,
  canRetry,
}: {
  attemptedRefreshToken: string;
  latestRefreshToken: string | null | undefined;
  isDefinitive: boolean;
  supportsLock: boolean;
  canRetry: boolean;
}): SwitchRefreshFailureDecision {
  if (latestRefreshToken && latestRefreshToken !== attemptedRefreshToken) {
    return canRetry ? 'retry-latest' : 'preserve';
  }
  if (!isDefinitive) return 'rethrow';
  return supportsLock ? 'clear' : 'preserve';
}

export function canCommitSwitchRefresh({
  startEpoch,
  currentEpoch,
  attemptedRefreshToken,
  currentRefreshToken,
}: {
  startEpoch: number;
  currentEpoch: number;
  attemptedRefreshToken: string;
  currentRefreshToken: string | null | undefined;
}): boolean {
  return (
    startEpoch === currentEpoch && attemptedRefreshToken === currentRefreshToken
  );
}

export function shouldApplyRemoteSwitchEnd({
  currentCookieFingerprint,
  currentAccessFingerprint,
  endedSessionFingerprint,
}: {
  currentCookieFingerprint: string | null;
  currentAccessFingerprint: string | null;
  endedSessionFingerprint: string;
}): boolean {
  // HttpOnly cookie가 없으면 어떤 local switch access도 갱신 불가능한 orphan이므로 정리한다.
  if (currentCookieFingerprint === null) return true;
  if (currentCookieFingerprint !== endedSessionFingerprint) {
    return false;
  }
  return (
    currentCookieFingerprint === endedSessionFingerprint ||
    currentAccessFingerprint === endedSessionFingerprint
  );
}

export async function runClearSwitchSessionLifecycle(actions: {
  cancelQueries: () => Promise<void>;
  shouldDeleteRefreshCookie: () => Promise<boolean>;
  clearLocalCredentialAndSpace: () => void;
  deleteRefreshCookie: () => Promise<void>;
  removeInactiveQueries: () => void;
  resetActiveQueries: () => Promise<void>;
  reportCleanupError?: (error: unknown) => void;
}): Promise<boolean> {
  // mutation interceptor가 다음 tick부터 operator header를 사용하도록 await 전에 내린다.
  actions.clearLocalCredentialAndSpace();
  await actions.cancelQueries();
  const ownsExpectedCookie = await actions.shouldDeleteRefreshCookie();
  if (ownsExpectedCookie) {
    try {
      await actions.deleteRefreshCookie();
    } catch (firstCookieError) {
      // server action의 일시 실패는 한 번 재시도하되, 실패해도 query cleanup을 계속한다.
      try {
        await actions.deleteRefreshCookie();
      } catch (retryCookieError) {
        actions.reportCleanupError?.(retryCookieError ?? firstCookieError);
      }
    }
  } else {
    // 새 session cookie가 이미 보이면 operator context로 active refetch하지 않는다.
    // inactive old cache만 제거하고 caller의 latest refresh/adopt가 active reset을 맡는다.
    actions.removeInactiveQueries();
    return false;
  }

  try {
    actions.removeInactiveQueries();
    await actions.resetActiveQueries();
  } catch (queryCleanupError) {
    actions.reportCleanupError?.(queryCleanupError);
  } finally {
    actions.removeInactiveQueries();
  }
  return true;
}

export async function runAccountSwitchEndLifecycle(actions: {
  requestEnd: () => Promise<void>;
  clearEndedSession: () => Promise<void>;
  isDefinitiveFailure: (error: unknown) => boolean;
}): Promise<void> {
  try {
    await actions.requestEnd();
  } catch (error) {
    if (!actions.isDefinitiveFailure(error)) throw error;
  }

  await actions.clearEndedSession();
}

export async function runAccountSwitchStartCommitLifecycle(actions: {
  createRefreshCookie: () => Promise<void>;
  canCommitAfterCookieWrite: () => Promise<boolean>;
  exposeSwitchAccessToken: () => void;
  rollbackFailedCommit: () => Promise<void>;
  cleanupQueries: () => Promise<void>;
  reportRollbackError?: (error: unknown) => void;
}): Promise<void> {
  try {
    await actions.createRefreshCookie();
    if (!(await actions.canCommitAfterCookieWrite())) {
      throw new Error('Account switch session changed during START commit.');
    }
    actions.exposeSwitchAccessToken();
  } catch (commitError) {
    try {
      await actions.rollbackFailedCommit();
    } catch (rollbackError) {
      actions.reportRollbackError?.(rollbackError);
    }
    throw commitError;
  } finally {
    await actions.cleanupQueries();
  }
}

export async function runAccountSwitchStartRequestLifecycle<T>(actions: {
  withLifecycleLock: (operation: () => Promise<void>) => Promise<void>;
  requestTokenPair: () => Promise<T>;
  commitTokenPair: (tokenPair: T) => Promise<void>;
}): Promise<void> {
  await actions.withLifecycleLock(async () => {
    const tokenPair = await actions.requestTokenPair();
    await actions.commitTokenPair(tokenPair);
  });
}

export function deferAccountScopedQueryReset(actions: {
  schedule: (callback: () => void) => void;
  resetQueries: () => Promise<void>;
  reportError?: (error: unknown) => void;
}): void {
  actions.schedule(() => {
    void actions.resetQueries().catch((error) => {
      actions.reportError?.(error);
    });
  });
}
